
import { useState, useEffect, useCallback } from 'react';
import { ReportTemplate } from '@/types/reportTypes';
import { useTemplateFilterLogic } from './useTemplateFilterLogic';

interface UseTemplateFiltersProps {
  initialCategories?: string[];
  initialSearchTerm?: string;
}

/**
 * Hook for managing template filter state (UI) and delegating filtering logic
 */
export function useTemplateFilters({
  initialCategories = ["All Categories"],
  initialSearchTerm = ""
}: UseTemplateFiltersProps = {}) {
  const [selectedCategories, setSelectedCategories] = useState<string[]>(initialCategories);
  const [searchTerm, setSearchTerm] = useState<string>(initialSearchTerm);
  const [filterError, setFilterError] = useState<Error | null>(null);

  const {
    handleCategoryChange: coreHandleCategoryChange,
    filterTemplates,
    error: logicError
  } = useTemplateFilterLogic();

  // Wrap handleCategoryChange so it sets state accordingly
  const handleCategoryChange = useCallback(
    (category: string, allCategoriesOption: string = "All Categories") => {
      const result = coreHandleCategoryChange(category, selectedCategories, allCategoriesOption);
      setSelectedCategories(result);
    },
    [coreHandleCategoryChange, selectedCategories]
  );

  // Clear error if filter state changes
  useEffect(() => {
    setFilterError(null);
  }, [selectedCategories, searchTerm]);

  // Hold any error found in the logic layer
  useEffect(() => {
    if (logicError) setFilterError(logicError);
  }, [logicError]);

  /**
   * Returns the filtered templates, still called as before
   */
  const getFilteredTemplates = useCallback(
    (templates: ReportTemplate[]) => filterTemplates(templates, selectedCategories, searchTerm),
    [filterTemplates, selectedCategories, searchTerm]
  );

  return {
    selectedCategories,
    setSelectedCategories,
    searchTerm,
    setSearchTerm,
    handleCategoryChange,
    filterTemplates: getFilteredTemplates,
    filterError
  };
}
